import { useEffect, useMemo, useState } from "react";

import { useGetSectionByIdQuery } from "@/redux/service.js";
import Question from "./Question.jsx";
import { SectionContentProps } from "@/features/survey/form/types";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import SectionFooter from "@/features/survey/form/components/SectionFooter";
import SectionHeader from "@/features/survey/form/components/SectionHeader";
import BaseRadioButtonsGroup from "@/components_v2/Radio/BaseRadioButtonsGroup.js";

const SectionContent = ({ sectionId }: SectionContentProps) => {
  const { data: section, isLoading } = useGetSectionByIdQuery(sectionId);
  const [activeTabKey, setActiveTabKey] = useState<string>("");

  const sectionTabs = useMemo(() => {
    if (!section) return [];

    return section.tabs.map((tab) => ({
      key: tab.title.toLowerCase(),
      label: tab.title,
      children: (
        <div className="flex flex-col gap-600">
          {tab.questions.map((i) => (
            <Question key={i} questionId={i} />
          ))}
        </div>
      ),
    }));
  }, [section]);

  useEffect(() => {
    if (sectionTabs.length === 0) return;
    const keyInTabs = sectionTabs.some((tab) => tab.key === activeTabKey);

    if (!activeTabKey || !keyInTabs) {
      setActiveTabKey(sectionTabs[0]?.key ?? "");
    }
  }, [sectionTabs, activeTabKey]);

  const effectiveActiveKey = activeTabKey || sectionTabs[0]?.key || "";
  const activeTabContent = sectionTabs.find(
    (tab) => tab.key === effectiveActiveKey,
  )?.children;

  if (isLoading) {
    return <BaseSpinner />;
  }

  return (
    section && (
      <div className="flex flex-col gap-600">
        <SectionHeader section={section} />
        <h6 className="body-m-regular">{section.helpText}</h6>
        {sectionTabs.length > 0 ? (
          <>
            <BaseRadioButtonsGroup
              block
              optionType="button"
              options={sectionTabs.map((tab) => ({
                label: tab.label,
                value: tab.key,
              }))}
              value={effectiveActiveKey}
              onChange={(e) => setActiveTabKey(e.target.value)}
            />
            {activeTabContent}
          </>
        ) : (
          <>
            {section.questions.map((i) => (
              <Question key={i} questionId={i} />
            ))}
          </>
        )}
        <SectionFooter section={section} />
      </div>
    )
  );
};

export default SectionContent;
